import { useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { esportaXLSX } from "../utils/esportaXLSX";
import { messaggioErrore } from "../utils/erroriUtente";
import { cercaAssegnazioni, etichettaMezzo } from "../utils/mezziProgrammazione";
import { segmentiGiornata } from "../utils/segmentiGiornata";

// ═══════════════════════════════════════════════════════════════════════════
// CHI AVEVA IL MEZZO — ricerca per i verbali (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Per titolare, gestione e amministrazione. Mette insieme tre fonti, senza sceglierne una:
//   1. Programmazione settimanale: a chi era assegnato il mezzo quel giorno (e su quale
//      commessa) — è il piano, non la prova di chi guidava;
//   2. Cartellino della persona quel giorno: orari e commesse timbrate, per ricostruire
//      a che ora era dove (un verbale ha un orario, la programmazione è per giorno);
//   3. Disponibilità mezzi: prese in carico e richieste confermate di quel mezzo.
// Il risultato è un aiuto per ricostruire, da verificare con le persone coinvolte.
// ═══════════════════════════════════════════════════════════════════════════

const MAX_SETTIMANE = 14; // circa tre mesi: il tempo entro cui di solito arriva un verbale
const pad2 = (n) => String(n).padStart(2, "0");
const dataStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const lunediDi = (d) => { const x = new Date(d); const g = x.getDay(); x.setDate(x.getDate() + (g === 0 ? -6 : 1 - g)); x.setHours(0, 0, 0, 0); return x; };
const settKey = (lun, orgId) => `${orgId}_${lun.getFullYear()}${pad2(lun.getMonth() + 1)}${pad2(lun.getDate())}`;
const fmt = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) : "";

function riassuntoTimbrature(giorno) {
  const t = giorno?.timbrature || [];
  if (!t.length) return "nessuna timbratura";
  // stesso calcolo del cartellino (utils/segmentiGiornata.js)
  const { segmenti: seg, apertaLavoro, apertaSpeciale } = segmentiGiornata(t);
  const segmenti = seg.map(x => `${x.entrata}–${x.uscita}${x.commessaNome ? ` ${x.commessaNome}` : ""}`);
  [apertaLavoro, apertaSpeciale].filter(Boolean).forEach(a => segmenti.push(`${a.ora}–(uscita mancante)${a.commessaNome ? ` ${a.commessaNome}` : ""}`));
  return segmenti.join(" · ");
}

export default function RicercaMezzoModal({ orgId, utenti = [], mezzi = [], onClose, addToast }) {
  const oggi = dataStr(new Date());
  const [mezzoId, setMezzoId] = useState("");
  const [dal, setDal] = useState(oggi);
  const [al, setAl] = useState(oggi);
  const [uid, setUid] = useState("");
  const [cercando, setCercando] = useState(false);
  const [risultati, setRisultati] = useState(null);
  const [errore, setErrore] = useState("");
  const nomeUtente = (u) => utenti.find(x => x.uid === u)?.displayName || "Utente non più attivo";

  const cerca = async () => {
    setErrore("");
    if (!dal || !al || al < dal) { setErrore("Controlla le date: la data finale non può precedere quella iniziale"); return; }
    const lun1 = lunediDi(new Date(dal + "T00:00:00")), lun2 = lunediDi(new Date(al + "T00:00:00"));
    const settimane = Math.round((lun2 - lun1) / (7 * 86400000)) + 1;
    if (settimane > MAX_SETTIMANE) { setErrore(`Periodo troppo lungo: al massimo ${MAX_SETTIMANE} settimane per ricerca`); return; }
    setCercando(true);
    try {
      // 1) Programmazione
      const celle = {};
      for (let l = new Date(lun1); l <= lun2; l.setDate(l.getDate() + 7)) {
        const s = await getDoc(doc(db, "programmazioniSettimanali", settKey(l, orgId)));
        if (s.exists()) Object.assign(celle, s.data().celle || {});
      }
      const righe = cercaAssegnazioni(celle, { mezzoId, dal, al, uid });

      // 2) Cartellini delle persone trovate, un mese alla volta
      const cacheCart = new Map();
      for (const r of righe) {
        const [a, m, g] = r.data.split("-").map(Number);
        const k = `${orgId}_${r.uid}_${a}_${m}`;
        if (!cacheCart.has(k)) {
          try { const c = await getDoc(doc(db, "presenze", k)); cacheCart.set(k, c.exists() ? c.data() : null); }
          catch { cacheCart.set(k, null); }
        }
        r.timbrature = riassuntoTimbrature(cacheCart.get(k)?.giorni?.[g]);
      }

      // 3) Disponibilità mezzi (prese in carico e richieste confermate che coprono il periodo)
      let movimenti = [];
      try {
        const vincoli = [where("orgId", "==", orgId)];
        if (mezzoId) vincoli.push(where("mezzoId", "==", mezzoId));
        const snap = await getDocs(query(collection(db, "richiesteRisorse"), ...vincoli));
        movimenti = snap.docs.map(d => d.data())
          .filter(x => x.mezzoId && ["confermata", "conclusa"].includes(x.stato))
          .filter(x => (x.dataInizio || "") <= al && (!x.dataFine || x.dataFine >= dal))
          .filter(x => !uid || x.richiedenteUid === uid)
          .sort((a, b) => (a.dataInizio || "").localeCompare(b.dataInizio || ""));
      } catch (e) { console.warn("Disponibilità mezzi non letta:", e); }

      setRisultati({ righe, movimenti });
    } catch (e) {
      addToast?.(messaggioErrore(e), "error");
    }
    setCercando(false);
  };

  const esporta = () => {
    if (!risultati) return;
    const mezzoDi = (r) => etichettaMezzo(mezzi.find(m => m.id === r.mezzoId) || r);
    const aoa = [["Fonte", "Data", "Mezzo", "Persona", "Commessa", "Timbrature del giorno", "Note"]];
    risultati.righe.forEach(r => aoa.push(["Programmazione", fmt(r.data), mezzoDi(r), nomeUtente(r.uid), r.commessaNome, r.timbrature || "", r.automatico ? "assegnato con autocompilazione" : "assegnato a mano"]));
    risultati.movimenti.forEach(x => aoa.push(["Disponibilità mezzi", `${fmt(x.dataInizio)}${x.dataFine ? ` → ${fmt(x.dataFine)}` : " → (restituzione non registrata)"}`,
      etichettaMezzo(mezzi.find(m => m.id === x.mezzoId) || { nome: x.mezzoNome }), x.richiedenteNome || "", x.projectName || "", "", x.diretto ? "presa in carico diretta" : "richiesta confermata"]));
    esportaXLSX(aoa, `Chi_aveva_il_mezzo_${dal}_${al}`, "Ricerca mezzi");
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>🔍 Chi aveva il mezzo</h3>
        <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 12, lineHeight: 1.5 }}>
          Programmazione, cartellini e Disponibilità mezzi messi insieme. È un aiuto per ricostruire chi guidava: la programmazione è il piano della giornata, verificalo con le persone coinvolte.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: "#6b7280" }}>Mezzo
            <select className="input" value={mezzoId} onChange={e => setMezzoId(e.target.value)}>
              <option value="">Tutti i mezzi</option>
              {mezzi.map(m => <option key={m.id} value={m.id}>{etichettaMezzo(m)}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 11, color: "#6b7280" }}>Dal
            <input className="input" type="date" value={dal} onChange={e => { setDal(e.target.value); if (al < e.target.value) setAl(e.target.value); }}/>
          </label>
          <label style={{ fontSize: 11, color: "#6b7280" }}>Al
            <input className="input" type="date" value={al} min={dal} onChange={e => setAl(e.target.value)}/>
          </label>
          <label style={{ fontSize: 11, color: "#6b7280" }}>Persona
            <select className="input" value={uid} onChange={e => setUid(e.target.value)}>
              <option value="">Tutte</option>
              {utenti.map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
            </select>
          </label>
        </div>
        {errore && <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 8 }}>{errore}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 12 }}>
          {risultati && (risultati.righe.length + risultati.movimenti.length) > 0 && <button className="btn" onClick={esporta}>📊 Esporta Excel</button>}
          <button className="btn" onClick={onClose}>Chiudi</button>
          <button className="btn btn-primary" onClick={cerca} disabled={cercando}>{cercando ? "Ricerca…" : "Cerca"}</button>
        </div>

        {risultati && (
          <div style={{ fontSize: 12.5 }}>
            <div style={{ fontWeight: 700, margin: "6px 0" }}>Programmazione ({risultati.righe.length})</div>
            {risultati.righe.length === 0
              ? <div style={{ color: "#9ca3af", marginBottom: 10 }}>Nessuna assegnazione trovata nel periodo.</div>
              : <div style={{ overflowX: "auto", marginBottom: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                    <thead><tr style={{ background: "#f9fafb", textAlign: "left" }}>
                      <th style={{ padding: "6px 8px" }}>Data</th><th style={{ padding: "6px 8px" }}>Mezzo</th><th style={{ padding: "6px 8px" }}>Persona</th>
                      <th style={{ padding: "6px 8px" }}>Commessa</th><th style={{ padding: "6px 8px" }}>Timbrature</th></tr></thead>
                    <tbody>{risultati.righe.map((r, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{fmt(r.data)}</td>
                        <td style={{ padding: "6px 8px" }}>{etichettaMezzo(mezzi.find(m => m.id === r.mezzoId) || r)}</td>
                        <td style={{ padding: "6px 8px", fontWeight: 600 }}>{nomeUtente(r.uid)}</td>
                        <td style={{ padding: "6px 8px" }}>{r.commessaNome}</td>
                        <td style={{ padding: "6px 8px", color: "#6b7280" }}>{r.timbrature}</td>
                      </tr>))}</tbody>
                  </table>
                </div>}
            <div style={{ fontWeight: 700, margin: "6px 0" }}>Disponibilità mezzi ({risultati.movimenti.length})</div>
            {risultati.movimenti.length === 0
              ? <div style={{ color: "#9ca3af" }}>Nessuna presa in carico registrata nel periodo.</div>
              : risultati.movimenti.map((x, i) => (
                <div key={i} style={{ padding: "4px 0", borderTop: "1px solid #f3f4f6" }}>
                  <strong>{x.richiedenteNome}</strong> — {etichettaMezzo(mezzi.find(m => m.id === x.mezzoId) || { nome: x.mezzoNome })} · dal {fmt(x.dataInizio)}{x.dataFine ? ` al ${fmt(x.dataFine)}` : " (restituzione non registrata)"}
                  <span style={{ color: "#9ca3af" }}> · {x.diretto ? "presa in carico diretta" : "richiesta confermata"}</span>
                </div>))}
          </div>
        )}
      </div>
    </div>
  );
}
